import 'src/styles/post-processor-queue.css';

import {
  faAlignLeft,
  faChevronDown,
  faInfoCircle,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import Loader from 'src/components/loader';
import { api } from 'src/utils/api';
import {
  API_RESOURCES,
  PRIORITIES,
  PRIORITIES_MAP,
  RUN_STATUS,
  RUN_STATUS_LABELS,
} from 'src/utils/constants';
import routes from 'src/utils/routes';
import { getRouteURI, getUuid } from 'src/utils/url';

const RUNS_PER_PAGE = 5;

/* eslint-disable camelcase */
const COPY = {
  printer: {
    empty: 'No processing runs are currently assigned to this printer.',
    atCapacity:
      'Printer capacity has been reached. New runs cannot be started at this time. Please wait until capacity becomes available and try again.',
  },
  post_processor: {
    empty: 'No processing runs are currently assigned to this post processor.',
    atCapacity:
      'Processor capacity has been reached. New runs cannot be started at this time. Please wait until capacity becomes available and try again.',
  },
};
/* eslint-enable camelcase */

const PRIORITY_CLASS = {
  [PRIORITIES.CRITICAL]: 'ppq-priority-badge--critical',
  [PRIORITIES.HIGH]: 'ppq-priority-badge--high',
  [PRIORITIES.LOW]: 'ppq-priority-badge--low',
};

const STATUS_CLASS = {
  [RUN_STATUS.queuedReady]: 'ppq-status-badge--queued-ready',
  [RUN_STATUS.queued]: 'ppq-status-badge--queued',
  [RUN_STATUS.inProgress]: 'ppq-status-badge--in-progress',
  [RUN_STATUS.paused]: 'ppq-status-badge--paused',
};

const TABLE_HEADERS = [
  'Pos.',
  'Run name',
  'Priority',
  'Status',
  'Scheduled start',
  'Scheduled end',
];

const formatEstimate = value => (value ? dayjs(value).format('YYYY/MM/DD, HH:mm') : '-');

const getRunMeta = run => {
  const priorityKey =
    Object.keys(PRIORITIES).find(key => PRIORITIES[key] === run.priority) || 'NORMAL';
  const priorityValue = PRIORITIES[priorityKey];
  return {
    priorityValue,
    priorityLabel: PRIORITIES_MAP[priorityValue]?.defaultMessage || 'Normal',
    priorityClass: PRIORITY_CLASS[priorityValue] || 'ppq-priority-badge--normal',
    statusClass: STATUS_CLASS[run.status] || 'ppq-status-badge--default',
    statusLabel: RUN_STATUS_LABELS[run.status],
  };
};

const fetchSchedules = async runList => {
  if (!runList?.length) return [];
  const response = await api
    .get(`${API_RESOURCES.SCHEDULE_RUNS}/`, {
      searchParams: { 'filter[run]': runList.map(run => run.uri).join(',') },
    })
    .json();
  return response.resources || [];
};

const RunQueue = ({ workstation: workstationProp, filterKey }) => {
  const { addToast } = useToasts();
  const copy = COPY[filterKey];

  const [workstation, setWorkstation] = useState(workstationProp);
  const [runs, setRuns] = useState([]);
  const [activeRuns, setActiveRuns] = useState([]);
  const [schedulesByRun, setSchedulesByRun] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [startingRuns, setStartingRuns] = useState(false);
  const [selectedRunUris, setSelectedRunUris] = useState([]);
  const [totalRuns, setTotalRuns] = useState(0);

  const workstationUri = workstation?.uri || workstationProp?.uri;
  const remainingCapacity =
    workstation?.in_progress_max > 0
      ? Math.max(0, workstation.in_progress_max - (workstation?.in_progress?.length || 0))
      : Infinity;
  const isAtCapacity =
    workstation?.in_progress_max > 0 &&
    workstation?.in_progress?.length >= workstation?.in_progress_max;
  const selectableRuns = runs.filter(run => run.status === RUN_STATUS.queuedReady);
  const isSelectAllDisabled =
    runs.length === 0 || (selectedRunUris.length === 0 && remainingCapacity === 0);

  const fetchRuns = useCallback(
    async (statusList, { offset, limit } = {}) => {
      const searchParams = {
        [`filter[${filterKey}]`]: workstationUri,
        'filter[status]': statusList.join(','),
        sort: 'workstation_queue_position',
      };
      if (limit != null) {
        searchParams['page[limit]'] = limit;
        searchParams['page[offset]'] = offset;
      }
      const response = await api.get(`${API_RESOURCES.RUN}/`, { searchParams }).json();
      const resources = response.resources || [];
      const scheduleRuns = await fetchSchedules(resources);
      if (scheduleRuns.length) {
        setSchedulesByRun(prev => ({
          ...prev,
          ...Object.fromEntries(scheduleRuns.map(scheduleRun => [scheduleRun.run, scheduleRun])),
        }));
      }
      return { resources, total: response.meta?.count || 0 };
    },
    [filterKey, workstationUri]
  );

  const loadQueuedRuns = useCallback(
    async (offset = 0) => {
      const { resources, total } = await fetchRuns([RUN_STATUS.queued, RUN_STATUS.queuedReady], {
        offset,
        limit: RUNS_PER_PAGE,
      });
      setRuns(prev => (offset ? [...prev, ...resources] : resources));
      setTotalRuns(total);
    },
    [fetchRuns]
  );

  useEffect(() => {
    setWorkstation(workstationProp);
  }, [workstationProp]);

  useEffect(() => {
    if (!workstationUri) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const [active, queued] = await Promise.all([
          fetchRuns([RUN_STATUS.inProgress, RUN_STATUS.paused]),
          fetchRuns([RUN_STATUS.queued, RUN_STATUS.queuedReady], {
            offset: 0,
            limit: RUNS_PER_PAGE,
          }),
        ]);
        if (cancelled) return;
        setActiveRuns(active.resources);
        setRuns(queued.resources);
        setTotalRuns(queued.total);
      } catch (error) {
        if (!cancelled) addToast('Failed to fetch runs queue', { appearance: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [addToast, fetchRuns, workstationUri]);

  const refresh = async () => {
    try {
      setWorkstation(await api.get(workstationUri, { prefixUrl: false }).json());
    } catch (error) {
      addToast('Failed to fetch workstation information', { appearance: 'error' });
    }
    try {
      const [active] = await Promise.all([
        fetchRuns([RUN_STATUS.inProgress, RUN_STATUS.paused]),
        loadQueuedRuns(),
      ]);
      setActiveRuns(active.resources);
    } catch (error) {
      addToast('Failed to fetch active runs', { appearance: 'error' });
    }
  };

  const isSelectable = run =>
    run.status === RUN_STATUS.queuedReady &&
    (selectedRunUris.includes(run.uri) || selectedRunUris.length < remainingCapacity);

  const handleSelectRun = run => {
    if (run.status !== RUN_STATUS.queuedReady) return;
    setSelectedRunUris(prev => {
      if (prev.includes(run.uri)) return prev.filter(uri => uri !== run.uri);
      if (prev.length < remainingCapacity) return [...prev, run.uri];
      return prev;
    });
  };

  const handleSelectAll = () => {
    const maxSelectable = Math.min(selectableRuns.length, remainingCapacity);
    setSelectedRunUris(
      selectedRunUris.length >= maxSelectable && selectedRunUris.length > 0
        ? []
        : selectableRuns.slice(0, remainingCapacity).map(run => run.uri)
    );
  };

  const handleStartRuns = async () => {
    setStartingRuns(true);
    try {
      await Promise.all(
        selectedRunUris.map(uri =>
          api.put(uri, { prefixUrl: false, json: { status: RUN_STATUS.inProgress } })
        )
      );
      addToast('Runs started successfully', { appearance: 'success' });
      setSelectedRunUris([]);
      await refresh();
    } catch (error) {
      addToast('Failed to start some runs', { appearance: 'error' });
    } finally {
      setStartingRuns(false);
    }
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      await loadQueuedRuns(runs.length);
    } catch (error) {
      addToast('Failed to fetch runs queue', { appearance: 'error' });
    } finally {
      setLoadingMore(false);
    }
  };

  const renderCheckbox = (run, testId) => (
    <Form.Check
      type='checkbox'
      checked={selectedRunUris.includes(run.uri)}
      className='ppq-select-run'
      data-testid={testId}
      disabled={!isSelectable(run)}
      onChange={() => handleSelectRun(run)}
    />
  );

  const renderRunRow = (run, index, isQueue) => {
    const meta = getRunMeta(run);
    const schedule = schedulesByRun[run.uri];
    return (
      <tr key={run.uri}>
        <td>{isQueue ? renderCheckbox(run, `run-queue-select-${run.uri}`) : null}</td>
        <td>{index + 1}</td>
        <td>
          <Link className='ppq-run-link' to={getRouteURI(routes.run, { uuid: getUuid(run.uri) })}>
            {run.name}
          </Link>
        </td>
        <td>
          <span className={`ppq-priority-badge ${meta.priorityClass}`}>{meta.priorityLabel}</span>
        </td>
        <td>
          <span className={`ppq-status-badge ${meta.statusClass}`}>{meta.statusLabel}</span>
        </td>
        <td>{formatEstimate(schedule?.estimates?.start)}</td>
        <td>{formatEstimate(schedule?.estimates?.end)}</td>
      </tr>
    );
  };

  const renderMobileCard = (run, index, isQueue) => {
    const meta = getRunMeta(run);
    const schedule = schedulesByRun[run.uri];
    return (
      <div key={run.uri} className='ppq-mobile-card'>
        <div className='ppq-mobile-card-header'>
          {isQueue ? renderCheckbox(run) : null}
          <span className='ppq-mobile-card-pos' onClick={() => isQueue && handleSelectRun(run)}>
            {`#${index + 1}`}
          </span>
          <span className={`ppq-priority-badge ${meta.priorityClass}`}>
            {meta.priorityLabel.slice(0, 1)}
          </span>
          <Link
            className='ppq-run-link ppq-mobile-card-name'
            to={getRouteURI(routes.run, { uuid: getUuid(run.uri) })}
          >
            {run.name}
          </Link>
          <span className={`ppq-status-badge ${meta.statusClass}`}>{meta.statusLabel}</span>
        </div>
        <div className='ppq-mobile-card-row'>
          <span className='ppq-mobile-card-label'>Scheduled start:</span>
          <span>{formatEstimate(schedule?.estimates?.start)}</span>
        </div>
        <div className='ppq-mobile-card-row'>
          <span className='ppq-mobile-card-label'>Scheduled end:</span>
          <span>{formatEstimate(schedule?.estimates?.end)}</span>
        </div>
      </div>
    );
  };

  const renderRunList = (runList, isQueue) => (
    <>
      <div className='table-responsive ppq-table-wrapper'>
        <table className='ppq-table'>
          <thead>
            <tr>
              <th>
                {isQueue && (
                  <Form.Check
                    type='checkbox'
                    checked={
                      selectableRuns.length > 0 &&
                      selectedRunUris.length > 0 &&
                      selectedRunUris.length === Math.min(selectableRuns.length, remainingCapacity)
                    }
                    className='ppq-select-all'
                    data-testid='run-queue-select-all'
                    disabled={isSelectAllDisabled}
                    onChange={handleSelectAll}
                  />
                )}
              </th>
              {TABLE_HEADERS.map(header => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runList.map((run, index) => renderRunRow(run, index, isQueue))}
            {isQueue && runList.length === 0 && (
              <tr>
                <td colSpan='7' className='text-center p-4'>
                  {copy.empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className='ppq-mobile-cards'>
        {runList.map((run, index) => renderMobileCard(run, index, isQueue))}
        {isQueue && runList.length === 0 && (
          <div className='ppq-mobile-empty'>No upcoming runs</div>
        )}
      </div>
    </>
  );

  if (loading) {
    return (
      <div className='run-queue' data-testid='run-queue'>
        <div className='ppq-queue-card'>
          <div className='ppq-queue-loading'>
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='run-queue' data-testid='run-queue'>
      <div className='ppq-queue-card'>
        <div className='ppq-queue-header'>
          <div className='ppq-queue-title'>
            <FontAwesomeIcon icon={faAlignLeft} />
            Run queue
          </div>
          <div className='ppq-queue-count'>
            Showing {runs.length + activeRuns.length} of {totalRuns + activeRuns.length}
          </div>
        </div>
        {activeRuns.length > 0 && (
          <>
            <div className='ppq-subsection-header'>
              Active runs ({workstation?.in_progress?.length || 0} of{' '}
              {workstation?.in_progress_max || 0} in progress)
            </div>
            {renderRunList(activeRuns, false)}
            <div className='ppq-subsection-header'>Upcoming runs</div>
          </>
        )}
        {isAtCapacity && (
          <div className='ppq-info-banner ppq-info-banner--warning'>
            <FontAwesomeIcon icon={faInfoCircle} className='ppq-info-banner-icon' />
            <span>{copy.atCapacity}</span>
          </div>
        )}
        {runs.some(run => run.status === RUN_STATUS.queued) && (
          <div className='ppq-info-banner'>
            <FontAwesomeIcon icon={faInfoCircle} className='ppq-info-banner-icon' />
            <span>
              Queued runs cannot be selected. Only runs in <strong>Queued Work Ready</strong> status
              are available for selection.
            </span>
          </div>
        )}
        {renderRunList(runs, true)}
        <div className='ppq-queue-footer'>
          <div className='ppq-footer-counts'>
            <div className='ppq-selected-count'>
              {selectedRunUris.length} run{selectedRunUris.length === 1 ? '' : 's'} selected
            </div>
            <div className='ppq-queue-count'>
              Showing {runs.length} of {totalRuns}
            </div>
          </div>
          <div className='ppq-footer-actions'>
            <Button
              variant='primary'
              size='sm'
              disabled={
                selectedRunUris.length === 0 ||
                startingRuns ||
                selectedRunUris.length > remainingCapacity
              }
              onClick={handleStartRuns}
            >
              {startingRuns ? (
                <Loader inline />
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlay} className='mr-1' />
                  Start selected runs
                </>
              )}
            </Button>
            {runs.length < totalRuns && (
              <Button
                variant='outline-primary'
                size='sm'
                disabled={loadingMore}
                onClick={handleLoadMore}
              >
                {loadingMore ? (
                  <Loader inline />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faChevronDown} className='mr-1' />
                    Load more runs
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

RunQueue.propTypes = {
  workstation: PropTypes.shape({
    uri: PropTypes.string,
    /* eslint-disable camelcase */
    in_progress: PropTypes.arrayOf(PropTypes.string),
    in_progress_max: PropTypes.number,
    /* eslint-enable camelcase */
  }).isRequired,
  filterKey: PropTypes.oneOf(['printer', 'post_processor']).isRequired,
};

export default RunQueue;
